import React, { forwardRef, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Slide from '@mui/material/Slide';
import { t } from '@cyberint/common-utils';
import { ModalModes } from './modal-consts';
import { Button } from '../Buttons/Button';
import Icons from '../Icons';
import { ButtonSizes, ButtonShapes } from '../Buttons/Button/button-consts';
import { stopPropagation } from '../../utils/listeners';

import * as S from './styled';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const Modal = ({
  mode = ModalModes.DARK,
  title,
  content,
  testId = 'GlobalModal',
  isOpen,
  size,
  onClose,
  onMinimize,
  minimizeText = t('buttons.minimize'),
  disabled,
  centralize = true,
  onOk,
  okText = t('buttons.save'),
  okTestId,
  cancelText,
  closeText,
  cancelTestId,
  additionalControls,
  isCloseOnBackdropClick,
  isLoading,
  isTransparent = true,
  fullScreen: initialFullScreen,
  enableFullScreen,
  className = 'dsc-Modal'
}) => {
  cancelText = cancelText || closeText;
  const okButtonRef = useRef(null);
  const [fullScreen, setFullScreen] = useState(initialFullScreen);

  const handleKeyDown = event => {
    if (event.key === 'Enter' && isOpen && onOk && !disabled) {
      event.preventDefault();
      onOk(event);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' && !isCloseOnBackdropClick) return;
    onClose ? onClose() : onMinimize?.();
  };

  const handleFullScreenToggle = () => {
    setFullScreen(prev => !prev);
  };

  const IconFullScreen = fullScreen ? Icons.Shrink : Icons.Expand;

  return (
    <S.Dialog
      onClick={stopPropagation}
      $isTransparent={isTransparent}
      open={isOpen}
      TransitionComponent={Transition}
      $size={size}
      $fullScreen={fullScreen}
      onClose={handleClose}
      data-testid={testId}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={['new-ui', mode, className].filter(Boolean).join(' ')}>
      {typeof title === 'string' || !title ? (
        <S.Title id="alert-dialog-title" $isTransparent={isTransparent}>
          <S.TitleText>{title}</S.TitleText>
          <S.ControlsContainer>
            {enableFullScreen && (
              <IconFullScreen
                color="var(--icon-default-color)"
                onClick={handleFullScreenToggle}
                $isFullScreen={fullScreen}
                data-testid="modal-fullscreen-toggle"
              />
            )}
            {onMinimize ? (
              <S.MinimizeIcon color="var(--icon-default-color)" onClick={onMinimize} tooltip={minimizeText} />
            ) : null}
            {onClose ? <S.CloseIcon color="var(--icon-default-color)" onClick={onClose} tooltip={cancelText} /> : null}
          </S.ControlsContainer>
        </S.Title>
      ) : (
        title
      )}
      <S.Content $centralize={centralize} $fullScreen={fullScreen} $enableFullScreen={enableFullScreen}>
        {typeof content === 'string' ? <S.Message id="alert-dialog-description">{content}</S.Message> : content}
      </S.Content>

      {additionalControls || onOk || cancelText ? (
        <S.Actions $isTransparent={isTransparent}>
          {!!additionalControls && <S.AdditionalControlsWrapper>{additionalControls}</S.AdditionalControlsWrapper>}
          {!!okText && !!onOk && (
            <Button
              testId={okTestId || `button_${okText}`}
              ref={okButtonRef}
              size={ButtonSizes.SMALL}
              onClick={onOk}
              disabled={disabled}
              shape={ButtonShapes.FILLED}
              loading={isLoading}>
              {okText}
            </Button>
          )}
          {!!cancelText && !!onClose && (
            <Button.Linked
              size={ButtonSizes.SMALL}
              onClick={onClose}
              autoFocus
              testId={cancelTestId || `button_${cancelText}`}>
              {cancelText}
            </Button.Linked>
          )}
        </S.Actions>
      ) : null}
    </S.Dialog>
  );
};

Modal.propTypes = {
  mode: PropTypes.oneOf(['dark-mode', 'light-mode']),
  isOpen: PropTypes.bool,
  onMinimize: PropTypes.func,
  minimizeText: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.element]),
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.element]),
  testId: PropTypes.string,
  disabled: PropTypes.bool,
  centralize: PropTypes.bool,
  onOk: PropTypes.func,
  okText: PropTypes.string,
  okTestId: PropTypes.string,
  onClose: PropTypes.func,
  closeText: PropTypes.string,
  cancelText: PropTypes.string,
  cancelTestId: PropTypes.string,
  additionalControls: PropTypes.node,
  isCloseOnBackdropClick: PropTypes.bool,
  size: PropTypes.shape({
    width: PropTypes.string,
    height: PropTypes.string
  }),
  isLoading: PropTypes.bool,
  isTransparent: PropTypes.bool,
  fullScreen: PropTypes.bool,
  enableFullScreen: PropTypes.bool,
  className: PropTypes.string
};

export default Modal;
